import { useEffect } from 'react';
import { LOCALES, type Locale } from '@rozman/shared';
import { useLocale } from '@/hooks/useLocalePath';

interface SeoProps {
  title: string;
  description?: string;
  /** Locale-less path, e.g. `/produkt/foo`. hreflang alternates are emitted for both. */
  path: string;
  image?: string | null;
  /** Product/Offer structured data, emitted when the page is a product. */
  structuredData?: Record<string, unknown>;
  noindex?: boolean;
}

/**
 * Per-page, per-locale title and meta description, canonical, `hreflang` for both locales,
 * and structured data. For a shop this is revenue, not decoration.
 */
export function Seo({ title, description, path, image, structuredData, noindex }: SeoProps): null {
  const locale = useLocale();

  useEffect(() => {
    // Avoid "Rozman · Rozman" on the homepage, where the title already is the brand.
    document.title = title === 'Rozman' ? 'Rozman — meble tapicerowane' : `${title} · Rozman`;
    document.documentElement.lang = locale;

    upsertMeta('name', 'description', description ?? '');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description ?? '');
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:locale', locale === 'pl' ? 'pl_PL' : 'uk_UA');
    if (image) upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'robots', noindex ? 'noindex,follow' : 'index,follow');

    const origin = window.location.origin;
    upsertLink('canonical', `${origin}/${locale}${path}`, null);
    for (const alt of LOCALES) upsertLink('alternate', `${origin}/${alt}${path}`, alt);
    upsertLink('alternate', `${origin}/pl${path}`, 'x-default');

    const existing = document.getElementById('rz-structured-data');
    if (existing) existing.remove();
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'rz-structured-data';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [title, description, path, image, structuredData, noindex, locale]);

  return null;
}

function upsertMeta(attr: 'name' | 'property', key: string, value: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.content = value;
}

function upsertLink(rel: string, href: string, hreflang: Locale | 'x-default' | null): void {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector<HTMLLinkElement>(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    if (hreflang) tag.hreflang = hreflang;
    document.head.appendChild(tag);
  }
  tag.href = href;
}
